import { assign, createMachine, fromPromise } from 'xstate'

import Api from '../../../api/_legacy'
import {
  UserInformationBasic,
  UserInformationBasicDfm,
  UserStatus
} from '../../../api/_legacy/generated-api'
import { createSearchIndex, fuzzySearch } from './actions'

export * from './actions'

export type User = UserInformationBasic & {
  isExpanded: boolean
}

export type UserState = User | UserInformationBasicDfm

export type OperationName = 'list' | 'edit'

export type Counts = {
  active: number
  all: number
  dormant: number
  locked: number
  pending: number
}

export type UsersState = {
  businessUnitId?: string
  clientId: string
  counts: Counts
  error?: any
  filterStatus?: UserStatus
  filteredUsers: UserState[]
  fuse?: any
  operation: OperationName
  selectedUser?: UserState
  users: UserState[]
}

type FetchUsersEvent = {
  clientId: string
  type: 'FETCH_USERS'
}

type FetchBusinessUnitUsersEvent = {
  businessUnitId: string
  type: 'FETCH_USERS'
}

type SearchUsersEvent = {
  searchTerm: string
  type: 'SEARCH_USERS'
}

type SelectUserEvent = {
  type: 'SELECT_USER'
  userName: string
}

type AddUserEvent = {
  type: 'ADD_USER'
}

type DeleteEvent = {
  type: 'DELETE'
}

type UnlockEvent = {
  type: 'UNLOCK'
}

type ActivateEvent = {
  type: 'ACTIVATE'
}

type UsersSavedEvent = {
  type: 'USER_SAVED'
}

type FilterByStatusEvent = {
  status: UserStatus
  type: 'FILTER_BY_STATUS'
}

type UsersEvent =
  | FetchUsersEvent
  | FetchBusinessUnitUsersEvent
  | SearchUsersEvent
  | SelectUserEvent
  | AddUserEvent
  | DeleteEvent
  | UnlockEvent
  | ActivateEvent
  | UsersSavedEvent
  | FilterByStatusEvent

export const usersMachine = createMachine(
  {
    types: {} as {
      context: UsersState
      events: UsersEvent
    },
    id: 'users-machine',
    initial: 'idle',
    context: {
      clientId: '',
      users: [],
      counts: {
        all: 0,
        active: 0,
        dormant: 0,
        pending: 0,
        locked: 0
      },
      filteredUsers: [],
      operation: 'list'
    },
    states: {
      idle: {
        on: {
          FETCH_USERS: {
            actions: ['setClientId', 'setBusinessUnitId'],
            target: 'usersLoading'
          }
        }
      },
      usersLoading: {
        invoke: {
          id: 'getUsers',
          src: 'fetchUsers',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'usersLoaded',
            actions: ['setUsers', 'getCounts', 'createSearchIndex']
          },
          onError: {
            target: 'usersLoadFailure',
            actions: assign({
              error: ({ event }) => event.error
            })
          }
        }
      },
      usersLoaded: {
        on: {
          SELECT_USER: {
            target: 'userSelected',
            actions: ['selectUser']
          },
          SEARCH_USERS: {
            actions: ['filterUsers'],
            target: 'usersLoaded'
          },
          FILTER_BY_STATUS: {
            actions: ['filterByStatus'],
            target: 'usersLoaded'
          },
          FETCH_USERS: {
            actions: ['setClientId', 'setBusinessUnitId'],
            target: 'usersLoading'
          },
          USER_SAVED: {
            target: 'usersLoading'
          }
        }
      },
      usersLoadFailure: {},
      userSelected: {
        on: {
          USER_SAVED: {
            actions: ['unselectUser'],
            target: 'usersLoading'
          },
          SELECT_USER: {
            target: 'userSelected',
            actions: ['selectUser']
          },
          SEARCH_USERS: {
            actions: ['unselectUser', 'filterUsers'],
            target: 'usersLoaded'
          },
          FILTER_BY_STATUS: {
            actions: ['unselectUser', 'filterByStatus'],
            target: 'usersLoaded'
          },
          DELETE: {
            target: 'deleteUser'
          },
          UNLOCK: {
            target: 'unlockUser'
          },
          ACTIVATE: {
            target: 'activateUser'
          }
        }
      },
      unlockUser: {
        invoke: {
          id: 'unlockUser',
          src: 'unlockUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'usersLoading',
            actions: ['unselectUser']
          },
          onError: {
            target: 'unlockUserFailure',
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      unlockUserFailure: {},
      activateUser: {
        invoke: {
          id: 'activateUser',
          src: 'activateUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'usersLoading',
            actions: ['unselectUser']
          },
          onError: {
            target: 'activateUserFailure',
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      activateUserFailure: {},
      deleteUser: {
        invoke: {
          id: 'deleteUser',
          src: 'deleteUser',
          input: ({ context, event }) => ({ context, event }),
          onDone: {
            target: 'usersLoading',
            actions: ['unselectUser']
          },
          onError: {
            target: 'deleteUserFailure',
            actions: assign({ error: ({ event }) => event.error })
          }
        }
      },
      deleteUserFailure: {}
    }
  },
  {
    actors: {
      deleteUser: fromPromise(async ({ input }: any) =>
        Api.usermanager.removeDelete(input.context.selectedUser.userName)
      ),
      unlockUser: fromPromise(async ({ input }: any) =>
        Api.usermanager.unlockDetail(input.context.selectedUser.userName)
      ),
      activateUser: fromPromise(async ({ input }: any) =>
        Api.usermanager.activateCreate(input.context.selectedUser.userName)
      )
    },
    actions: {
      createSearchIndex: assign(createSearchIndex),
      setUsers: assign(({ context, event }: any) => {
        const users = event.output.filter(x => x.status !== 'Closed')
        return {
          ...context,
          users: [...users],
          filteredUsers: [...users]
        }
      }),
      setClientId: assign(({ context, event }: any) => ({
        ...context,
        clientId: event.clientId
      })),
      setBusinessUnitId: assign(({ context, event }: any) => ({
        ...context,
        businessUnitId: event.businessUnitId
      })),
      getCounts: assign(({ context }) => ({
        ...context,
        counts: {
          all: (context.users ?? []).length,
          active: (context.users ?? []).filter(x => x.status === 'Active').length,
          dormant: (context.users ?? []).filter(x => x.status === 'Inactive').length,
          pending: (context.users ?? []).filter(x => x.status === 'Pending').length,
          locked: (context.users ?? []).filter(x => x.status === 'Locked').length
        }
      })),
      unselectUser: assign(({ context }) => {
        const newUsers = [...context.users]
        newUsers.forEach(x => ((x as User).isExpanded = false))

        return {
          ...context,
          users: newUsers,
          selectedUser: undefined,
          operation: 'list'
        }
      }),
      selectUser: assign(({ context, event }: any) => {
        if (!event.userName) {
          throw new Error('userName not supplied')
        }

        const newUsers = [...context.users]
        const index = newUsers.findIndex(x => x.userName === event.userName)
        const user = { ...newUsers[index] }

        if ((user as User).isExpanded) {
          ;(user as User).isExpanded = false
          newUsers[index] = user

          return {
            ...context,
            users: newUsers,
            selectedUser: undefined,
            operation: 'list' as OperationName
          }
        }

        ;(user as User).isExpanded = true

        newUsers.filter((_, i) => i !== index).forEach(x => ((x as User).isExpanded = false))

        newUsers[index] = user

        const newState = {
          ...context,
          users: newUsers,
          selectedUser: context.users.find(x => x.userName === event.userName),
          operation: 'edit' as OperationName
        }

        return newState
      }),
      filterUsers: assign(fuzzySearch),
      filterByStatus: assign(({ context, event }: any) => {
        if (event.status === 'All') {
          return {
            ...context,
            filterStatus: undefined,
            filteredUsers: [...context.users]
          }
        }

        return {
          ...context,
          filterStatus: event.status,
          filteredUsers: (context.users ?? []).filter(x => x.status === event.status)
        }
      })
    }
  }
)
